import React from 'react';
import { getInitials } from '../../utils/commonUtils';
import { ErrorCircle16Filled, PersonStarburst20Filled } from '@fluentui/react-icons';
import { Tooltip } from '@fluentui/react-components';
import styles from './css/index.module.css';
import LastUpdatedCell from './LastUpdatedCell';

const ProjectTableView = ({
    projects,
    onEdit,
    onArchive,
    deleteModalRef,
    calculateColorSM,
    user,
    handleChangeProject,
    formatLastUpdated,
    isTableColumnVisible,
    getStatusColor,
}) => {
    const [filteredProjects, setFilteredProjects] = React.useState(projects);

    // Column configuration - new order
    const columns = [
        { key: 'status', label: 'Status', sortable: false, minWidth: 120 },
        { key: 'project_name_type', label: 'Project Name & Type', sortable: false, minWidth: 200 },
        { key: 'project_deadline', label: 'Project Deadline', sortable: false, width: 120 },
        { key: 'target_investor', label: 'Target & Investor', sortable: false, minWidth: 200 },
        { key: 'task_status', label: 'Task Status', sortable: false, minWidth: 150 },
        { key: 'key_task', label: 'Key Task', sortable: false, minWidth: 320 },
        { key: 'last_updated', label: 'Last updated', sortable: false, minWidth: 250 },
    ];

    // Get display value for a field
    const getDisplayValue = (project, field) => {
        switch (field) {
            case 'status':
                const status = project.status || '--';
                return (
                    <div
                        className={styles.statusChip}
                        style={{
                            backgroundColor: getStatusColor(status),
                            opacity: status === '--' ? 0.5 : 1,
                        }}>
                        {status}
                    </div>
                );
            case 'project_name_type':
                // Empty for now - will be designed later
                return (
                    <div className="d-flex align-items-center justify-content-between">
                        <div>
                            <p className={styles.primaryTableText}>{project.project_name}</p>
                            <p className={styles.secondaryTableText}>{project.project_type}</p>
                        </div>
                        {project?.vdr?.vdr_admin === user?.id && <PersonStarburst20Filled color="#FFC107" />}
                    </div>
                );
            case 'project_deadline':
                // Only show overdue icon if deadline has passed
                return (
                    <div className="d-flex align-items-center gap-1 justify-content-center">
                        {(() => {
                            if (!project.project_deadline) return null;
                            const deadline = new Date(project.project_deadline);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            return deadline < today ? <ErrorCircle16Filled color="var(--RAG-Red)" /> : null;
                        })()}
                        <p className={styles.primaryTableText}>{project.project_deadline}</p>
                    </div>
                );
            case 'target_investor':
                // Empty for now - will be designed later
                return (
                    <div>
                        <p className={styles.primaryTableText}>{project?.company_name || '--'}</p>
                        <p className={styles.secondaryTableText}>{project?.investor_name || '--'}</p>
                    </div>
                );
            case 'task_status': {
                // Proportional status bar (stuck, working_on_it, completed, not_started, cancelled)
                const summary = project?.projectprogress?.task_summary;
                if (!summary || !summary.total || summary.total <= 0) {
                    return (
                        <Tooltip
                            withArrow
                            positioning="above"
                            relationship="description"
                            content={<div>No tasks available</div>}>
                            <div style={{ alignSelf: 'stretch', height: 29, display: 'inline-flex', width: '100%', cursor: 'pointer' }}>
                                <div style={{ flex: '1 1 0', background: '#C0C6D8' }} />
                            </div>
                        </Tooltip>
                    );
                }
                return (
                    <Tooltip
                        withArrow
                        positioning="above"
                        relationship="description"
                        content={
                            <div style={{ padding: '4px 0' }}>
                                <div style={{ marginBottom: '4px' }}>Total Tasks: {summary.total}</div>
                                <div style={{ marginBottom: '4px' }}>Completed: {summary.completed || 0}</div>
                                <div style={{ marginBottom: '4px' }}>Working on it: {summary.working_on_it || 0}</div>
                                <div style={{ marginBottom: '4px' }}>Stuck: {summary.stuck || 0}</div>
                                <div style={{ marginBottom: '4px' }}>Not Started: {summary.not_started || 0}</div>
                                <div>Cancelled: {summary.cancelled || 0}</div>
                            </div>
                        }>
                        <div style={{ alignSelf: 'stretch', height: 29, display: 'inline-flex', width: '100%', cursor: 'pointer' }}>
                            {summary.stuck > 0 && (
                                <div
                                    style={{
                                        width: `${(summary.stuck / summary.total) * 100}%`,
                                        background: 'var(--RAG-Red)',
                                    }}
                                />
                            )}
                            {summary.working_on_it > 0 && (
                                <div
                                    style={{
                                        width: `${(summary.working_on_it / summary.total) * 100}%`,
                                        background: 'var(--RAG-Amber)',
                                    }}
                                />
                            )}
                            {summary.completed > 0 && (
                                <div
                                    style={{
                                        width: `${(summary.completed / summary.total) * 100}%`,
                                        background: 'var(--RAG-Green)',
                                    }}
                                />
                            )}
                            {summary.not_started > 0 && (
                                <div
                                    style={{
                                        width: `${(summary.not_started / summary.total) * 100}%`,
                                        background: '#C0C6D8',
                                    }}
                                />
                            )}
                            {summary.cancelled > 0 && (
                                <div
                                    style={{
                                        width: `${(summary.cancelled / summary.total) * 100}%`,
                                        background: '#989BAD',
                                    }}
                                />
                            )}
                            {summary.no_status > 0 && (
                                <div
                                    style={{
                                        width: `${(summary.no_status / summary.total) * 100}%`,
                                        background: '#6C757D',
                                    }}
                                />
                            )}
                        </div>
                    </Tooltip>
                );
            }
            case 'key_task':
                // Empty for now - will be designed later
                return (
                    <div className="d-flex align-items-center gap-2 justify-content-between w-100">
                        <div className="d-flex gap-1 flex-grow-1">
                            <div>
                                {project.projectprogress?.key_task_info?.assigned_to ? (
                                    <div className={styles.customAvatarTable}>{getInitials(project.projectprogress?.key_task_info?.assigned_to || '')}</div>
                                ) : (
                                    '--'
                                )}
                            </div>
                            <p className={styles.primaryTableText}>{project.projectprogress?.key_task}</p>
                        </div>
                        <div style={{ width: 210 }} className="d-flex align-items-center gap-1 justify-content-end">
                            {(() => {
                                if (!project.projectprogress?.key_task_info?.deadline) return null;
                                const deadline = new Date(project.projectprogress?.key_task_info?.deadline);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return deadline < today ? <ErrorCircle16Filled color="var(--RAG-Red)" /> : null;
                            })()}
                            <p className={styles.primaryTableText}>{project.projectprogress?.key_task_info?.deadline || '--'}</p>
                        </div>
                    </div>
                );
            case 'last_updated':
                // Use LastUpdatedCell component
                return (
                    <LastUpdatedCell
                        project={project}
                        formatLastUpdated={formatLastUpdated}
                        calculateColorSM={calculateColorSM}
                        dev={Number(project?.projectprogress?.development_score)}
                        adequate={Number(project?.projectprogress?.adequate_score)}
                        exemplary={Number(project?.projectprogress?.exemplary_score)}
                        irlOutstanding={Number(project?.irl_outstanding)}
                        irlCompleted={Number(project?.irl_completed)}
                        handleEditProject={onEdit}
                        handleArchiveProject={onArchive}
                        deleteModalRef={deleteModalRef}
                    />
                );
            default:
                return <div></div>;
        }
    };

    // Update filtered projects when projects prop changes
    React.useEffect(() => {
        setFilteredProjects(projects);
    }, [projects]);

    const handleNavigate = projectId => {
        handleChangeProject(projectId);
    };

    return (
        <div style={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
            <div className={styles.tableContainer}>
                <table className="w-100">
                    <thead className={styles.tableHeader}>
                        <tr>
                            {columns
                                .filter(c => !isTableColumnVisible || isTableColumnVisible(c.key))
                                .map(column => (
                                    <th
                                        key={column.key}
                                        className={styles.tableHeaderCell}
                                        style={{
                                            minWidth: column.minWidth,
                                            maxWidth: column.maxWidth || 'none',
                                            width: column.width,
                                        }}>
                                        <div className={styles.headerContent}>
                                            <span>{column.label}</span>
                                            {/* Sort icons removed as requested */}
                                        </div>
                                    </th>
                                ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProjects.map(project => (
                            <tr key={project.id} className={styles.tableRow} onClick={() => handleNavigate(project.id)} style={{ cursor: 'pointer' }}>
                                {columns
                                    .filter(c => !isTableColumnVisible || isTableColumnVisible(c.key))
                                    .map(column => (
                                        <td key={column.key} className={styles.cell}>
                                            <div className={styles.cellContent}>{getDisplayValue(project, column.key)}</div>
                                        </td>
                                    ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {filteredProjects?.length === 0 && (
                <div className={styles.emptyState}>
                    <h5>No projects found</h5>
                    <p>Try adjusting your filters or add a new project.</p>
                </div>
            )}
        </div>
    );
};

export default ProjectTableView;
